import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  withArrow?: boolean;
  bounce?: boolean;
  href?: string;
  children: React.ReactNode;
}
export function Button({
  variant = 'primary',
  size = 'md',
  withArrow = false,
  bounce = false,
  href,
  children,
  className = '',
  onClick,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-full font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer';
  const variants = {
    primary: 'bg-[#c9a227] text-white hover:bg-[#b08d1f] shadow-lg hover:shadow-xl focus:ring-[#c9a227]',
    secondary: 'bg-[#1a4d3e] text-white hover:bg-[#143d31] shadow-md hover:shadow-lg focus:ring-[#1a4d3e]',
    outline: 'border-2 border-[#1a4d3e] text-[#1a4d3e] hover:bg-[#1a4d3e] hover:text-white focus:ring-[#1a4d3e]'
  };
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg md:text-xl'
  };
  const bounceAnimation = bounce ? {
    y: [0, -8, 0],
    transition: {
      duration: 0.6,
      repeat: Infinity,
      repeatDelay: 1.5,
      ease: 'easeInOut'
    }
  } : {};
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (href) {
      e.preventDefault();
      // Check if it's an external URL
      if (href.startsWith('http://') || href.startsWith('https://')) {
        window.location.href = href;
      } else {
        // Internal anchor link
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }
    }
    onClick?.(e);
  };
  return <motion.button animate={bounceAnimation} whileHover={{
    scale: 1.05
  }} whileTap={{
    scale: 0.95
  }} className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`} onClick={handleClick} {...props} data-id="element-213">
      {children}
      {withArrow && <ArrowRight className="ml-2 h-5 w-5" data-id="element-214" />}
    </motion.button>;
}